// Place the cart as one real order — through the BFF's `POST /api/checkout`
// (#248, ADR 0055), which forwards it to the load-generator's `POST /`
// server-side with the `x-load-generator-secret`. Same-origin, cookie auth;
// the browser carries no secret.

export interface OrderItem {
  sku: string;
  quantity: number;
  unitPrice: number;
}

// The load-generator's `count: 1` + `order` override slice — the BFF passes
// the body through unchanged (services/load-generator/openapi.yaml).
interface LoadRequest {
  count: 1;
  order: {
    customerId: string;
    items: OrderItem[];
  };
}

// services/load-generator/run.go Result
export interface RunResult {
  orderIds: string[];
}

export type PlaceOrderResult =
  | { kind: "placed"; orderId: string }
  | { kind: "error"; message: string }
  // No session — the caller should send the visitor to sign in.
  | { kind: "unauthorized" };

export async function placeOrder(order: {
  customerId: string;
  items: OrderItem[];
}): Promise<PlaceOrderResult> {
  const body: LoadRequest = { count: 1, order };

  let res: Response;
  try {
    res = await fetch("/api/checkout", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? err.message : "request failed (network)",
    };
  }

  if (res.status === 401) {
    return { kind: "unauthorized" };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (res.status !== 200) {
    const apiError =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `${res.status} ${res.statusText}`.trim();
    return { kind: "error", message: apiError };
  }

  const orderId = (payload as RunResult | null)?.orderIds?.[0];
  if (!orderId) {
    return { kind: "error", message: "the order was accepted but no order id came back" };
  }
  return { kind: "placed", orderId };
}
